// The admission timeline — assembled by query over the source tables, never
// stored, never duplicated.
//
// Every row is clickable through to the record behind it, which is the whole
// justification for a timeline existing: it is a navigation surface over the
// clinical record, not a second narrative of it.
//
// Loads 50 at a time. A three-week stay has thousands of events, and rendering
// them all to show the most recent twenty is exactly what the performance rule
// forbids.
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { NativeSelect } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import { Timeline, type TimelineEntry } from '@/components/ui/Timeline';
import { useTimeline } from '../hooks';
import type { SourceRef, TimelineItem, TimelineType } from '../types';
import { Section, fmtDateTime } from '../ui';

const FILTERS: { label: string; types?: TimelineType[] }[] = [
  { label: 'Everything' },
  { label: 'Clinical notes', types: ['progress_note', 'nursing_note', 'assessment'] },
  { label: 'Medications', types: ['medication_order', 'medication_administration'] },
  { label: 'Observations', types: ['vitals'] },
  { label: 'Investigations & procedures', types: ['investigation', 'procedure'] },
  { label: 'Consults & care plan', types: ['consult', 'care_plan'] },
  { label: 'Admission events', types: ['admission', 'transfer', 'document'] },
];

const CATEGORY_VARIANT: Record<string, 'neutral' | 'info' | 'success' | 'warn' | 'danger' | 'caution'> = {
  admission: 'neutral',
  documentation: 'info',
  nursing: 'info',
  observation: 'neutral',
  medication: 'caution',
  investigation: 'warn',
  procedure: 'warn',
  consult: 'info',
  'care-plan': 'success',
  document: 'neutral',
};

function toEntry(item: TimelineItem, onOpen?: (r: SourceRef) => void): TimelineEntry {
  return {
    key: `${item.type}-${item.id}`,
    at: fmtDateTime(item.at),
    title: item.title,
    badge: <Badge variant={CATEGORY_VARIANT[item.category] ?? 'neutral'}>{item.category}</Badge>,
    detail: item.detail,
    meta: item.author,
    onClick: onOpen ? () => onOpen(item.source) : undefined,
  };
}

export function TimelineSection({ admissionId, onOpen }: {
  admissionId: number; onOpen?: (r: SourceRef) => void;
}) {
  const [filterIdx, setFilterIdx] = useState(0);
  const [limit, setLimit] = useState(50);
  const filter = FILTERS[filterIdx];
  const { data, isLoading, isFetching } = useTimeline(admissionId, {
    types: filter?.types,
    limit,
  });

  return (
    <Section
      focusId="transfer document admission"
      title="Clinical timeline"
      subtitle="Assembled live from the records below it — nothing here is a copy. Select any entry to open its source."
      action={
        <NativeSelect className="h-7 w-auto text-xs" value={String(filterIdx)}
          onChange={e => { setFilterIdx(Number(e.target.value)); setLimit(50); }}>
          {FILTERS.map((f, i) => <option key={f.label} value={i}>{f.label}</option>)}
        </NativeSelect>
      }
    >
      {isLoading ? <LoadingState label="Assembling timeline…" />
        : (
          <>
            <Timeline entries={(data?.data ?? []).map(item => toEntry(item, onOpen))} emptyLabel="Nothing recorded in this view yet" />
            {data?.has_more && (
              <div className="mt-3 text-center">
                <Button variant="ghost" size="sm" loading={isFetching}
                  onClick={() => setLimit(l => l + 50)}>
                  Load older entries
                </Button>
              </div>
            )}
          </>
        )}
    </Section>
  );
}
